import { keyframes } from '@emotion/react';
import { Box, Stack, styled } from '@mui/material';

import { Button, theme as ds } from '@/design-system';
import { forMobile } from '@/styles/responsive';

const revealItem = (maxHeight: number) =>
  keyframes({
    from: { opacity: 0, maxHeight: 0, transform: 'translateY(-4px)' },
    to: { opacity: 1, maxHeight, transform: 'none' },
  });

const collapseItem = (maxHeight: number) =>
  keyframes({
    from: { opacity: 1, maxHeight, transform: 'none' },
    to: { opacity: 0, maxHeight: 0, transform: 'translateY(-4px)' },
  });

const revealCard = revealItem(800);

const revealRow = revealItem(200);

const collapseCard = collapseItem(800);

const collapseRow = collapseItem(200);

const CONNECTOR_ELBOW_WIDTH = ds.spacing[40];

export const ShareClassCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$exiting',
})<{ $exiting?: boolean }>(({ $exiting }) => ({
  padding: `${ds.spacing[24]}px`,
  borderRadius: `${ds.radius[16]}px`,
  backgroundColor: ds.colors.background.raised,
  overflow: 'hidden',
  animation: `${$exiting ? collapseCard : revealCard} ${ds.motion.reveal}`,
  animationFillMode: $exiting ? 'forwards' : 'none',

  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
}));

export const ShareClassFields = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) 40px',
  alignItems: 'flex-end',
  gap: `${ds.spacing[12]}px`,
  width: '100%',
});

export const ChangedOutline = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$color',
})<{ $color?: string }>(({ $color }) => ({
  borderRadius: `${ds.radius[12]}px`,
  ...($color && { outline: `1px solid ${$color}`, outlineOffset: 2 }),
}));

export const AllocationSummary = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: `${ds.spacing[16]}px`,
});

export const ShareholderRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$exiting',
})<{ $exiting?: boolean }>(({ theme, $exiting }) => ({
  position: 'relative',
  display: 'grid',
  gridTemplateColumns: 'repeat(5, minmax(0, 1fr)) 40px',
  gap: `${ds.spacing[16]}px`,
  alignItems: 'start',
  animation: `${$exiting ? collapseRow : revealRow} ${ds.motion.reveal}`,
  animationFillMode: $exiting ? 'forwards' : 'none',

  [forMobile(theme)]: {
    minWidth: 920,
  },

  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },

  '&::before': {
    content: '""',
    position: 'absolute',
    left: -CONNECTOR_ELBOW_WIDTH - ds.spacing[12],
    top: '50%',
    width: CONNECTOR_ELBOW_WIDTH,
    borderTop: `1px solid ${ds.colors.border.default}`,
    transform: 'translateY(-50%)',
  },
}));

export const ShareholderRowCell = styled(Box)({
  marginTop: `${ds.spacing[32]}px`,
});

export const ShareholderGroup = styled(Box)({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: `${ds.spacing[16]}px`,
  marginLeft: `${ds.spacing[8]}px`,
  paddingLeft: `${ds.spacing[64]}px`,
  marginTop: `${ds.spacing[8]}px`,

  '&::before': {
    content: '""',
    position: 'absolute',
    left: `${ds.spacing[12]}px`,
    top: `-${ds.spacing[12]}px`,
    bottom: `${ds.spacing[20]}px`,
    width: 0,
    borderLeft: `1px solid ${ds.colors.border.default}`,
  },
});

export const AddShareholderButton = styled(Button)(({ theme }) => ({
  position: 'relative',
  width: 'fit-content',
  marginTop: `${ds.spacing[16]}px`,

  '&::before': {
    content: '""',
    position: 'absolute',
    left: -CONNECTOR_ELBOW_WIDTH - ds.spacing[12],
    top: '50%',
    width: CONNECTOR_ELBOW_WIDTH,
    borderTop: `1px solid ${ds.colors.border.default}`,
    transform: 'translateY(-50%)',
  },

  [forMobile(theme)]: {
    marginLeft: 0,
  },
}));
